import { useState, useCallback } from 'react';
import { TextField, Button, Banner, Select, Autocomplete, Icon, Text } from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createTestShipment,
  syncFulfillmentService,
  registerCarrierService,
  fetchShopStatus,
  fetchBindingsForSimulation,
  simulateWebhook,
  JITSU_EVENT_TYPES,
  type BindingForSimulation,
} from '../utils/api';

export default function ToolsPage() {
  const [orderId, setOrderId] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');

  // Webhook simulation state
  const [bindingSearch, setBindingSearch] = useState('');
  const [selectedBinding, setSelectedBinding] = useState<BindingForSimulation | null>(null);
  const [selectedEventType, setSelectedEventType] = useState('');

  const { data: status } = useQuery({
    queryKey: ['shopStatus'],
    queryFn: fetchShopStatus,
  });

  // Fetch bindings for webhook simulation
  const { data: bindingsData } = useQuery({
    queryKey: ['bindingsForSimulation', bindingSearch],
    queryFn: () => fetchBindingsForSimulation(bindingSearch || undefined),
  });

  const carrierServiceEnabled = status?.features?.carrierServiceEnabled ?? false;

  const shipmentMutation = useMutation({
    mutationFn: createTestShipment,
  });

  const syncMutation = useMutation({
    mutationFn: syncFulfillmentService,
  });

  const carrierMutation = useMutation({
    mutationFn: registerCarrierService,
  });

  const webhookMutation = useMutation({
    mutationFn: simulateWebhook,
  });

  const handleCreateShipment = useCallback(() => {
    if (!orderId) return;
    shipmentMutation.mutate({ orderId, trackingNumber: trackingNumber || undefined });
  }, [orderId, trackingNumber, shipmentMutation]);

  const handleSync = useCallback(() => {
    syncMutation.mutate();
  }, [syncMutation]);

  const handleRegisterCarrier = useCallback(() => {
    carrierMutation.mutate();
  }, [carrierMutation]);

  const handleSimulateWebhook = useCallback(() => {
    if (!selectedBinding || !selectedEventType) return;
    webhookMutation.mutate({
      bindingId: selectedBinding.id,
      eventType: selectedEventType,
    });
  }, [selectedBinding, selectedEventType, webhookMutation]);

  // Build autocomplete options for bindings
  const bindingOptions = (bindingsData?.bindings || []).map((b) => ({
    value: b.id,
    label: `${b.orderName || b.orderId} - ${b.status}${b.trackingNumbers?.[0] ? ` (${b.trackingNumbers[0]})` : ''}`,
  }));

  // Group event types by category for the select
  const eventTypeOptions = [
    { label: 'Select an event...', value: '' },
    ...JITSU_EVENT_TYPES.map((e) => ({
      label: `${e.category}: ${e.label}`,
      value: e.value,
    })),
  ];

  return (
    <div>
      {/* Sync Shipping Service */}
      <div className="jitsu-card">
        <h3 className="jitsu-card-title">Sync Shipping Service</h3>
        <p style={{ color: 'var(--jitsu-mid-grey)', marginBottom: '16px' }}>
          Re-register the Jitsu shipping service and location with Shopify.
          Use this if the service was accidentally deleted or needs to be updated.
        </p>

        {syncMutation.isSuccess && (
          <div style={{ marginBottom: '12px' }}>
            <Banner tone="success" onDismiss={() => syncMutation.reset()}>
              Shipping service synced successfully.
            </Banner>
          </div>
        )}

        {syncMutation.isError && (
          <div style={{ marginBottom: '12px' }}>
            <Banner tone="critical" onDismiss={() => syncMutation.reset()}>
              Sync failed: {(syncMutation.error as Error).message}
            </Banner>
          </div>
        )}

        <Button onClick={handleSync} loading={syncMutation.isPending}>
          Sync Shipping Service
        </Button>
      </div>

      {/* Register Carrier Service - only shown when feature is enabled */}
      {carrierServiceEnabled && (
        <div className="jitsu-card">
          <h3 className="jitsu-card-title">Register Carrier Service</h3>
          <p style={{ color: 'var(--jitsu-mid-grey)', marginBottom: '16px' }}>
            Register Jitsu as a carrier service with Shopify. This enables Jitsu shipping
            rates to appear as delivery options during customer checkout.
          </p>

          {carrierMutation.isSuccess && (
            <div style={{ marginBottom: '12px' }}>
              <Banner tone="success" onDismiss={() => carrierMutation.reset()}>
                {carrierMutation.data?.message || 'Carrier service registered successfully.'}
              </Banner>
            </div>
          )}

          {carrierMutation.isError && (
            <div style={{ marginBottom: '12px' }}>
              <Banner tone="critical" onDismiss={() => carrierMutation.reset()}>
                Registration failed: {(carrierMutation.error as Error).message}
              </Banner>
            </div>
          )}

          <Button onClick={handleRegisterCarrier} loading={carrierMutation.isPending}>
            Register Carrier Service
          </Button>
        </div>
      )}

      {/* Create Test Shipment */}
      <div className="jitsu-card">
        <h3 className="jitsu-card-title">Create Test Shipment</h3>
        <p style={{ color: 'var(--jitsu-mid-grey)', marginBottom: '16px' }}>
          Manually create a shipment for testing purposes. This will create a
          shipment record with the specified tracking information.
        </p>

        {shipmentMutation.isSuccess && (
          <div style={{ marginBottom: '12px' }}>
            <Banner tone="success" onDismiss={() => shipmentMutation.reset()}>
              <div>
                <p>Shipment created successfully!</p>
                <p style={{ fontSize: '13px', marginTop: '4px' }}>
                  Shipment ID: {shipmentMutation.data?.fulfillmentId}
                </p>
                <p style={{ fontSize: '13px' }}>
                  Tracking: {shipmentMutation.data?.trackingNumber}
                </p>
              </div>
            </Banner>
          </div>
        )}

        {shipmentMutation.isError && (
          <div style={{ marginBottom: '12px' }}>
            <Banner tone="critical" onDismiss={() => shipmentMutation.reset()}>
              Failed to create shipment: {(shipmentMutation.error as Error).message}
            </Banner>
          </div>
        )}

        <div style={{ maxWidth: '400px' }}>
          <div style={{ marginBottom: '12px' }}>
            <TextField
              label="Order ID"
              value={orderId}
              onChange={setOrderId}
              autoComplete="off"
              placeholder="gid://shopify/Order/123456789"
              helpText="The Shopify order GID or numeric ID"
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <TextField
              label="Tracking Number (optional)"
              value={trackingNumber}
              onChange={setTrackingNumber}
              autoComplete="off"
              placeholder="JITSU123456789"
              helpText="Leave blank to auto-generate"
            />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              variant="primary"
              onClick={handleCreateShipment}
              loading={shipmentMutation.isPending}
              disabled={!orderId}
            >
              Create Shipment
            </Button>
            <Button
              onClick={() => {
                setOrderId('');
                setTrackingNumber('');
                shipmentMutation.reset();
              }}
            >
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Webhook Simulator */}
      <div className="jitsu-card">
        <h3 className="jitsu-card-title">Webhook Simulator</h3>
        <p style={{ color: 'var(--jitsu-mid-grey)', marginBottom: '16px' }}>
          Simulate Jitsu webhook events for a specific shipment. This is useful for testing
          how your integration handles different tracking events without waiting for real deliveries.
        </p>

        {webhookMutation.isSuccess && (
          <div style={{ marginBottom: '12px' }}>
            <Banner tone="success" onDismiss={() => webhookMutation.reset()}>
              <div>
                <p>{webhookMutation.data?.message}</p>
                <p style={{ fontSize: '13px', marginTop: '4px' }}>
                  Previous status: {webhookMutation.data?.previousStatus}
                </p>
              </div>
            </Banner>
          </div>
        )}

        {webhookMutation.isError && (
          <div style={{ marginBottom: '12px' }}>
            <Banner tone="critical" onDismiss={() => webhookMutation.reset()}>
              Simulation failed: {(webhookMutation.error as Error).message}
            </Banner>
          </div>
        )}

        <div style={{ maxWidth: '500px' }}>
          {/* Binding Search */}
          <div style={{ marginBottom: '12px' }}>
            <Autocomplete
              options={bindingOptions}
              selected={selectedBinding ? [selectedBinding.id] : []}
              onSelect={(selected) => {
                const binding = bindingsData?.bindings.find((b) => b.id === selected[0]);
                setSelectedBinding(binding || null);
              }}
              textField={
                <Autocomplete.TextField
                  label="Select Shipment"
                  value={bindingSearch}
                  onChange={setBindingSearch}
                  placeholder="Search by order name or tracking number..."
                  autoComplete="off"
                  prefix={<Icon source={SearchIcon} />}
                />
              }
            />
            {selectedBinding && (
              <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f6f6f7', borderRadius: '4px' }}>
                <Text as="p" variant="bodySm">
                  <strong>Selected:</strong> {selectedBinding.orderName || selectedBinding.orderId}
                </Text>
                <Text as="p" variant="bodySm">
                  Status: {selectedBinding.status} | Tracking: {selectedBinding.trackingNumbers?.[0] || 'N/A'}
                </Text>
              </div>
            )}
          </div>

          {/* Event Type Select */}
          <div style={{ marginBottom: '16px' }}>
            <Select
              label="Event Type"
              options={eventTypeOptions}
              value={selectedEventType}
              onChange={setSelectedEventType}
              helpText="Select the Jitsu event to simulate"
            />
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              variant="primary"
              onClick={handleSimulateWebhook}
              loading={webhookMutation.isPending}
              disabled={!selectedBinding || !selectedEventType}
            >
              Simulate Webhook
            </Button>
            <Button
              onClick={() => {
                setSelectedBinding(null);
                setBindingSearch('');
                setSelectedEventType('');
                webhookMutation.reset();
              }}
            >
              Clear
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

